import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Dialog, Grid, IconButton, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PersonAddAlt1Outlined, SaveOutlined, SearchOutlined } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import CustomTextField from "../../common/CustomTextField";
import { FieldLabel, getIsrCommissionTypeKey } from "../../../utils/Utility";
import { useModalStyles } from "../../common/useModalStyles";
import { useNavigation } from "../../../hooks/useNavigation";
import { IsrFormValues, IsrRecord, IsrUserEmail } from "./isrTypes";
import { ISR_COMMISSION_TYPE } from "../../../constants/applicationConstants";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { useIsrReducer } from "../../../redux/slice/isr/isrSlice";
import { getIsrUserDetails } from "../../../service/IsrService";
import { getAgencyDataById } from "../../../service/AgencyService";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";

interface IsrPopupProps {
  open: boolean;
  isrRecord?: IsrRecord | null;
  onClose: () => void;
  onSubmitForm: (data: IsrFormValues) => void;
}

const emptyValues: IsrFormValues = {
  userId: undefined,
  agencyName: "",
  email: "",
  phone: "",
  streetAddress: "",
  unitNumber: "",
  zipcode: "",
  city: "",
  state: "",
  commissionType: ISR_COMMISSION_TYPE,
  active: true,
};

const IsrPopup: React.FC<IsrPopupProps> = ({ open, isrRecord, onClose, onSubmitForm }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const { navigate } = useNavigation();
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<IsrFormValues>({
    defaultValues: emptyValues,
  });
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const isrCommissionTypeKey = useMemo(
    () => getIsrCommissionTypeKey(allCodeTablesValuesMap),
    [allCodeTablesValuesMap]
  );
  const { isrUserEmailList, fetchIsrUserEmailListStatus } = useIsrReducer();
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IsrUserEmail | null>(null);
  const [unknownEmailMessage, setUnknownEmailMessage] = useState("");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const typedEmail = watch("email");

  const isEdit = !!isrRecord;
  const showNameStep = isEdit || detailsVisible;
  const title = isEdit ? "Edit ISR" : "Add ISR";
  const subtitle = isEdit
    ? "Update ISR name."
    : showNameStep
      ? "Enter ISR name."
      : "Search by user email.";

  const emailOptions = useMemo(
    () => (isrUserEmailList || []).filter((row) => row?.email),
    [isrUserEmailList]
  );

  useEffect(() => {
    if (!open) {
      reset(emptyValues);
      setDetailsVisible(false);
      setDetailsLoading(false);
      setSelectedUser(null);
      setUnknownEmailMessage("");
      setShowCreateUser(false);
      return;
    }
    if (isrRecord) {
      setDetailsVisible(true);
      reset({
        ...emptyValues,
        agencyName: isrRecord.fullName,
        email: isrRecord.email,
        active: isrRecord.active,
      });
      getAgencyDataById(isrRecord.isrId).then((response) => {
        const agency = response?.data?.data;
        if (!agency) {
          return;
        }
        reset({
          ...emptyValues,
          agencyName: agency.agencyName || isrRecord.fullName,
          email: agency.email || isrRecord.email,
          phone: agency.phone || "",
          streetAddress: agency.streetAddress || "",
          unitNumber: agency.unitNumber || "",
          zipcode: agency.zipcode || "",
          city: agency.city || "",
          state: agency.state || "",
          commissionType: agency.commissionType || isrCommissionTypeKey,
          active: isrRecord.active,
        });
      }).catch(() => undefined);
      return;
    }
    reset(emptyValues);
    setDetailsVisible(false);
    setSelectedUser(null);
    setUnknownEmailMessage("");
    setShowCreateUser(false);
  }, [open, isrRecord, reset, isrCommissionTypeKey]);

  const applyUserDetails = async (userId?: number, email?: string, fallbackName?: string) => {
    if (!userId && !email) {
      return;
    }
    setDetailsLoading(true);
    try {
      const response = await getIsrUserDetails({ userId, email });
      const details = response?.data?.code === SUCCESS_CODE ? response.data.data : null;
      const options = { shouldValidate: true, shouldDirty: true };
      setValue("userId", details?.userId ?? userId, options);
      setValue("agencyName", details?.agencyName || fallbackName || "", options);
      setValue("email", details?.email || email || "", options);
      setValue("phone", details?.phone || "", options);
      setValue("streetAddress", details?.streetAddress || "", options);
      setValue("unitNumber", details?.unitNumber || "", options);
      setValue("zipcode", details?.zipcode || "", options);
      setValue("city", details?.city || "", options);
      setValue("state", details?.state || "", options);
      setValue("commissionType", isrCommissionTypeKey, options);
      setDetailsVisible(true);
    } catch {
      setDetailsVisible(true);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSearch = async () => {
    const email = (typedEmail || "").trim();
    if (!email) {
      setValue("email", email, { shouldValidate: true });
      return;
    }
    const match = selectedUser && selectedUser.email.toLowerCase() === email.toLowerCase()
      ? selectedUser
      : emailOptions.find((option) => option.email.toLowerCase() === email.toLowerCase());
    if (match) {
      setUnknownEmailMessage("");
      setShowCreateUser(false);
      await applyUserDetails(match.userId, match.email, match.fullName);
      return;
    }
    reset({ ...emptyValues, email });
    setDetailsVisible(false);
    setUnknownEmailMessage("This email is not an existing user. Create a new user to continue.");
    setShowCreateUser(true);
  };

  const handleBackToEmail = () => {
    const email = (typedEmail || "").trim();
    reset({ ...emptyValues, email });
    setDetailsVisible(false);
    setDetailsLoading(false);
    setSelectedUser(null);
    setUnknownEmailMessage("");
    setShowCreateUser(false);
  };

  const handleDialogClose = () => {
    reset(emptyValues);
    setDetailsVisible(false);
    setSelectedUser(null);
    setUnknownEmailMessage("");
    setShowCreateUser(false);
    onClose();
  };

  const handleCreateUser = () => {
    handleDialogClose();
    navigate("/user_create");
  };

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmitForm)}
        sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <PersonAddAlt1Outlined sx={{ fontSize: 20, color: tokens.accent }} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
          }}
        >
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={showNameStep ? 12 : 8}>
              <FieldLabel label="Email" required tokens={tokens} />
              <Box sx={inputFieldSx(!!errors.email)}>
                {showNameStep ? (
                  <CustomTextField
                    control={control}
                    controllerName="email"
                    label=""
                    size="small"
                    isDisabled
                    rules={{
                      required: "Email is required.",
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                        message: "Invalid email.",
                      },
                    }}
                  />
                ) : (
                  <Controller
                    name="email"
                    control={control}
                    rules={{ required: "Email is required." }}
                    render={({ field }) => (
                      <Autocomplete
                        options={emailOptions}
                        loading={fetchIsrUserEmailListStatus === "loading" || detailsLoading}
                        getOptionLabel={(option) => typeof option === "string" ? option : option.email}
                        isOptionEqualToValue={(option, value) => option.userId === value.userId}
                        filterOptions={(options, { inputValue }) => {
                          const query = inputValue.trim().toLowerCase();
                          if (!query) {
                            return options;
                          }
                          return options.filter((option) => (
                            option.email.toLowerCase().includes(query)
                            || option.fullName.toLowerCase().includes(query)
                          ));
                        }}
                        freeSolo
                        inputValue={field.value || ""}
                        onInputChange={(_, value, reason) => {
                          if (reason === "reset") {
                            return;
                          }
                          field.onChange(value);
                          if (reason === "input" || reason === "clear") {
                            setSelectedUser(null);
                            setUnknownEmailMessage("");
                            setShowCreateUser(false);
                          }
                        }}
                        onChange={(_, value) => {
                          if (!value) {
                            setSelectedUser(null);
                            field.onChange("");
                            return;
                          }
                          if (typeof value === "string") {
                            setSelectedUser(null);
                            field.onChange(value);
                            return;
                          }
                          setSelectedUser(value);
                          field.onChange(value.email);
                          setUnknownEmailMessage("");
                          setShowCreateUser(false);
                        }}
                        renderOption={(props, option: IsrUserEmail) => (
                          <li {...props} key={option.userId}>
                            {option.email}{option.fullName ? ` (${option.fullName})` : ""}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Type to filter existing user emails"
                            error={!!errors.email}
                            helperText={errors.email?.message as string}
                          />
                        )}
                      />
                    )}
                  />
                )}
              </Box>
            </Grid>
            {!isEdit && !showNameStep && (
              <Grid item xs={12} sm={4}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSearch}
                  sx={{
                    ...primaryBtnSx,
                    width: "100%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.8,
                    px: 2,
                    py: 1.15,
                    border: "none",
                    cursor: "pointer",
                    color: "#fff",
                  }}
                >
                  <SearchOutlined sx={{ fontSize: "1.05rem" }} />
                  Search
                </Box>
              </Grid>
            )}
            {!isEdit && !showNameStep && unknownEmailMessage && (
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "0.8rem", color: tokens.error || "#ef4444" }}>
                  {unknownEmailMessage}
                </Typography>
              </Grid>
            )}
            {!isEdit && !showNameStep && showCreateUser && (
              <Grid item xs={12} sm={4}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCreateUser}
                  sx={{
                    ...primaryBtnSx,
                    width: "100%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.8,
                    px: 2,
                    py: 1.15,
                    border: "none",
                    cursor: "pointer",
                    color: "#fff",
                  }}
                >
                  <PersonAddAlt1Outlined sx={{ fontSize: "1.05rem" }} />
                  Create User
                </Box>
              </Grid>
            )}
            {showNameStep && (
              <Grid item xs={12}>
                <FieldLabel label="ISR Name" required tokens={tokens} />
                <Box sx={inputFieldSx(!!errors.agencyName)}>
                  <CustomTextField
                    control={control}
                    controllerName="agencyName"
                    label=""
                    size="small"
                    rules={{ required: "ISR Name is required." }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexDirection: { xs: "column", sm: "row" } }}>
            {showNameStep && (
              <Box
                component="button"
                type="submit"
                sx={{
                  ...primaryBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                <SaveOutlined sx={{ fontSize: "1.1rem" }} />
                {isEdit ? "Update ISR" : "Save ISR"}
              </Box>
            )}
            <Box
              component="button"
              type="button"
              onClick={!isEdit && showNameStep ? handleBackToEmail : handleDialogClose}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
              }}
            >
              {!isEdit && showNameStep ? "Back" : "Cancel"}
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default IsrPopup;
